import { useState } from "react";
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LinearProgress from '@mui/material/LinearProgress';
import "./Overview.css";
import "./custom.css";
import PieChartCard from "../../../components/Charts/PieChartCard";
import { Link } from "react-router-dom";
import { COLORS } from '../../../lib/appColors';
import { useAppContext } from '../../../lib/contextLib';
import { useBudgetOverview } from "./useBudgetOverview";
import SummaryCard from '../../../components/SummaryCard';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';

interface FreqOption {
  key: string;
  label: string;
  field: string;
}

const FREQ_OPTIONS: FreqOption[] = [
  { key: 'week',      label: 'Weekly',       field: 'amountWeek' },
  { key: 'fortnight', label: 'Fortnightly',   field: 'amountFortnight' },
  { key: 'month',     label: 'Monthly',       field: 'amountMonth' },
  { key: 'year',      label: 'Yearly',        field: 'amountYear' },
];

interface CardConfig {
  typeTotal: string;
  label: string;
  color: string | null;
}

const CARD_CONFIG: CardConfig[] = [
  { typeTotal: 'Income',   label: 'Income',   color: COLORS.income },
  { typeTotal: 'Savings',  label: 'Savings',  color: COLORS.savings },
  { typeTotal: 'Expenses', label: 'Expenses', color: COLORS.expense },
  { typeTotal: 'Leftover', label: 'Leftover', color: null },
];

const TYPE_COLORS: Record<string, string> = {
  income: COLORS.income,
  savings: COLORS.savings,
  expense: COLORS.expense,
};

function fmtDollar(v: number): string {
  return '$' + Math.round(v).toLocaleString('en-AU');
}

export default function Overview() {
  const { savingsTarget } = useAppContext();
  const {
    isAuthenticated, isLoading, summaryLoading, summaryError,
    budgetTotals, rechartsIncome, rechartsSavings, rechartsExpenses, dataOverall,
    savingsRate, topItems, expenseBarData, ruleComparison,
  } = useBudgetOverview();
  const [distributionSelection, setDistributionSelection] = useState('income');
  const [freq, setFreq] = useState('month');

  const freqField = FREQ_OPTIONS.find(o => o.key === freq)?.field ?? 'amountMonth';

  if (!isAuthenticated) {
    return (
      <div className="Overview">
        <div className="lander">
          <h1>Making Budgets</h1>
          <p className="text-muted">A simple tool for making budgets</p>
        </div>
      </div>
    );
  }

  const distData  = distributionSelection === 'income'  ? rechartsIncome
                  : distributionSelection === 'savings' ? rechartsSavings
                  : rechartsExpenses;
  const distTitle = distributionSelection === 'income'  ? 'Income Distribution'
                  : distributionSelection === 'savings' ? 'Savings Distribution'
                  : 'Expenses Distribution';

  const distBtnSx = (key: string, color: string) => distributionSelection === key
    ? { bgcolor: color, '&:hover': { bgcolor: color + 'cc' } }
    : { color, borderColor: color, '&:hover': { borderColor: color, bgcolor: color + '15' } };

  // Savings rate color: green if >= target, amber if >= half target, red if below
  const rateColor = savingsRate >= savingsTarget ? COLORS.savings : savingsRate >= savingsTarget / 2 ? COLORS.expense : '#ef5350';

  return (
    <div className="Overview">
      <div className="container pt-1 pb-4">
        <div className="budgetItems">
          <Paper elevation={0} sx={{ background: 'transparent', p: 0 }}>
            <div className="overview-block">
              <div className="stack-lg overview-inner">

                {/* Frequency switcher */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                  <ToggleButtonGroup
                    value={freq}
                    exclusive
                    onChange={(_, v) => v && setFreq(v)}
                    size="small"
                    sx={{
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      '& .MuiToggleButton-root': {
                        px: { xs: 1, sm: 2.5 },
                        py: 0.5,
                        fontWeight: 600,
                        fontSize: { xs: '.65rem', sm: '.72rem' },
                        letterSpacing: '.5px',
                        textTransform: 'uppercase',
                      },
                    }}
                  >
                    {FREQ_OPTIONS.map(o => (
                      <ToggleButton key={o.key} value={o.key}>{o.label}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                {/* Summary cards */}
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap' }}>
                  {CARD_CONFIG.map(card => {
                    const total = budgetTotals.find(t => t.typeTotal === card.typeTotal);
                    return (
                      <SummaryCard
                        key={card.typeTotal}
                        label={card.label}
                        value={total?.[freqField] as number ?? 0}
                        color={card.color}
                        isLoading={isLoading}
                      />
                    );
                  })}
                </Box>

                {/* Savings Rate widget */}
                {!isLoading && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: '12px 16px', sm: '16px 20px' },
                      borderColor: rateColor + '55',
                      background: rateColor + '0a',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ flex: '0 0 auto', textAlign: 'center', minWidth: 80 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: rateColor, lineHeight: 1 }}>
                        {savingsRate.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600, color: rateColor, fontSize: '.65rem' }}>
                        Savings Rate
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Box sx={{ position: 'relative' }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, savingsTarget > 0 ? (savingsRate / savingsTarget) * 100 : 0)}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            bgcolor: 'action.hover',
                            '& .MuiLinearProgress-bar': { bgcolor: rateColor, borderRadius: 5 },
                          }}
                        />
                        {/* Target marker at 100% position (= 20% savings rate) */}
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: -1,
                            width: 2,
                            height: 12,
                            bgcolor: 'text.disabled',
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '.6rem' }}>0%</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '.6rem', color: savingsRate >= savingsTarget ? rateColor : 'inherit' }}>
                          {savingsTarget}% target
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )}

                {/* Action buttons */}
                <Paper
                  variant="outlined"
                  className="overview-actions"
                  sx={{ p: 1, display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap', justifyContent: 'center', bgcolor: 'background.paper', '& .MuiButton-root': { fontWeight: 600, letterSpacing: '.5px' } }}
                >
                  <Button size="small" variant="outlined" component={Link} to="/budget/income/new"  sx={{ color: COLORS.income,  borderColor: COLORS.income,  '&:hover': { borderColor: COLORS.income,  bgcolor: 'rgba(63,164,246,.08)'  } }}>ADD INCOME</Button>
                  <Button size="small" variant="outlined" component={Link} to="/budget/savings/new" sx={{ color: COLORS.savings, borderColor: COLORS.savings, '&:hover': { borderColor: COLORS.savings, bgcolor: 'rgba(108,207,125,.08)' } }}>ADD SAVING</Button>
                  <Button size="small" variant="outlined" component={Link} to="/budget/expenses/new" sx={{ color: COLORS.expense, borderColor: COLORS.expense, '&:hover': { borderColor: COLORS.expense, bgcolor: 'rgba(255,179,71,.1)'  } }}>ADD EXPENSE</Button>
                </Paper>
              </div>

              <hr />

              {/* Charts section */}
              <div className="overview-charts-wrap" style={{ marginBottom: '0.75rem' }}>
                <div className="overview-charts-grid" style={{ padding: '0 1.25rem' }}>

                  {/* Allocation donut */}
                  <div className="overview-chart-card" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="d-flex justify-content-center mb-2 chart-toggle-bar" style={{ width: '100%', marginBottom: '.75rem' }}>
                      <Stack
                        className="alloc-stack"
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
                        justifyContent="center"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        sx={{
                          width: '100%',
                          '& .alloc-label': { textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600, fontSize: '.75rem', color: 'var(--color-text, inherit)', display: 'block', textAlign: 'center', mb: { xs: .5, sm: 0 } },
                          '& .MuiButton-root': { fontWeight: 600, letterSpacing: '.5px' },
                          '& .alloc-toggle': { flex: { xs: '1 1 auto', sm: '0 0 auto' }, width: { xs: '100%', sm: 'auto' } },
                        }}
                      >
                        <span className="alloc-label">{summaryLoading ? 'Allocation (loading\u2026)' : 'Allocation:'}</span>
                        <Button className="alloc-toggle" size="small" variant="contained" disabled>Income</Button>
                      </Stack>
                    </div>
                    <PieChartCard title="Income Allocation" data={dataOverall} donut height={450} externalLabels hideHeader />
                  </div>

                  {/* Distribution pie */}
                  <div className="overview-chart-card" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="d-flex justify-content-center mb-2 chart-toggle-bar" style={{ width: '100%', marginBottom: '.75rem' }}>
                      <Stack
                        className="dist-stack"
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
                        justifyContent="center"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        sx={{
                          width: '100%',
                          '& .dist-label': { textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600, fontSize: '.75rem', color: 'var(--color-text, inherit)', display: 'block', textAlign: 'center', mb: { xs: .5, sm: 0 } },
                          '& .MuiButton-root': { fontWeight: 600, letterSpacing: '.5px' },
                          '& .dist-toggle': { flex: { xs: '1 1 auto', sm: '0 0 auto' }, width: { xs: '100%', sm: 'auto' } },
                        }}
                      >
                        <span className="dist-label">{summaryLoading ? 'Distribution (loading\u2026)' : 'Distribution:'}</span>
                        <Button className="dist-toggle" size="small" variant={distributionSelection === 'income'  ? 'contained' : 'outlined'} onClick={() => setDistributionSelection('income')}   sx={distBtnSx('income',   '#3fa4f6')}>Income</Button>
                        <Button className="dist-toggle" size="small" variant={distributionSelection === 'savings' ? 'contained' : 'outlined'} onClick={() => setDistributionSelection('savings')}  sx={distBtnSx('savings',  '#6ccf7d')}>Savings</Button>
                        <Button className="dist-toggle" size="small" variant={distributionSelection === 'expenses'? 'contained' : 'outlined'} onClick={() => setDistributionSelection('expenses')} sx={distBtnSx('expenses', '#ffb347')}>Expenses</Button>
                      </Stack>
                    </div>
                    {distData.length ? (
                      <PieChartCard title={distTitle} data={distData} donut height={450} externalLabels hideHeader />
                    ) : (
                      <div className="text-center text-muted small w-100 d-flex align-items-center justify-content-center" style={{ padding: '2rem 0' }}>
                        {summaryLoading ? 'Loading\u2026' : 'No data'}
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* New widgets row */}
              {!isLoading && (
                <div className="overview-charts-wrap">
                  <div className="overview-charts-grid" style={{ padding: '0 1.25rem' }}>

                    {/* Expense Breakdown bar chart */}
                    <div className="overview-chart-card">
                      <div className="chart-surface" style={{ height: 380, padding: '0.75rem 0.75rem 0.25rem' }}>
                        <h5 style={{ fontSize: '.9rem', textAlign: 'center', margin: '0 0 .5rem' }}>Expense Breakdown</h5>
                        {expenseBarData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={320}>
                            <BarChart
                              data={expenseBarData}
                              layout="vertical"
                              margin={{ top: 4, right: 12, bottom: 0, left: 4 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(125,125,125,.12)" horizontal={false} />
                              <XAxis
                                type="number"
                                tickFormatter={(v: number) => fmtDollar(v)}
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                              />
                              <YAxis
                                type="category"
                                dataKey="name"
                                width={100}
                                tick={{ fontSize: 11 }}
                                tickLine={false}
                                axisLine={false}
                              />
                              <Tooltip
                                formatter={(value) => [fmtDollar(Number(value)) + '/yr', 'Amount']}
                                contentStyle={{ fontSize: 12, background: 'var(--color-surface-alt, #202733)', border: '1px solid var(--color-border, #2d3642)' }}
                              />
                              <Bar dataKey="amount" radius={[0, 4, 4, 0]} maxBarSize={24}>
                                {expenseBarData.map((_, i) => (
                                  <Cell key={i} fill={i === 0 ? COLORS.expense : COLORS.expense + '88'} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="text-muted small" style={{ padding: '2rem', textAlign: 'center' }}>No expense data</div>
                        )}
                      </div>
                    </div>

                    {/* Top 5 Largest Items + 50/30/20 Rule */}
                    <div className="overview-chart-card">
                      <div className="chart-surface" style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

                        {/* Top 5 Largest Items */}
                        <div>
                          <h5 style={{ fontSize: '.9rem', textAlign: 'center', margin: '0 0 .75rem' }}>Top 5 Largest Items</h5>
                          {topItems.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              {topItems.map((item, i) => {
                                const barWidth = topItems[0].yearlyAmount > 0
                                  ? (item.yearlyAmount / topItems[0].yearlyAmount) * 100
                                  : 0;
                                const color = TYPE_COLORS[item.type] || '#8c96d7';
                                return (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '.75rem', fontWeight: 600, minWidth: 20, textAlign: 'right', opacity: 0.45 }}>
                                      {i + 1}
                                    </Typography>
                                    <Box sx={{ flex: 1, position: 'relative', minHeight: 28 }}>
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          height: '100%',
                                          width: `${barWidth}%`,
                                          bgcolor: color + '20',
                                          borderRadius: 1,
                                          transition: 'width .3s ease',
                                        }}
                                      />
                                      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, height: 28 }}>
                                        <Typography sx={{ fontSize: '.78rem', fontWeight: 500 }}>
                                          {item.name}
                                          <Typography component="span" sx={{ fontSize: '.65rem', ml: 0.75, opacity: 0.45, fontWeight: 400 }}>
                                            {item.frequency}
                                          </Typography>
                                        </Typography>
                                        <Typography sx={{ fontSize: '.78rem', fontWeight: 600, color }}>
                                          {fmtDollar(item.yearlyAmount)}/yr
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <div className="text-muted small" style={{ textAlign: 'center' }}>No items</div>
                          )}
                        </div>

                        {/* Budget Health: Savings vs Expenses */}
                        <div>
                          <h5 style={{ fontSize: '.9rem', textAlign: 'center', margin: '0 0 .75rem' }}>Budget Health</h5>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {ruleComparison.map(rule => (
                              <Box key={rule.label}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                  <Typography sx={{ fontSize: '.75rem', fontWeight: 600, color: rule.color }}>
                                    {rule.label}
                                  </Typography>
                                  <Typography sx={{ fontSize: '.72rem', opacity: 0.7 }}>
                                    {rule.actual.toFixed(1)}%
                                    {rule.guideline > 0 && (
                                      <Typography component="span" sx={{ fontSize: '.65rem', ml: 0.5, opacity: 0.5 }}>
                                        / {rule.guideline}% guideline
                                      </Typography>
                                    )}
                                  </Typography>
                                </Box>
                                <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      height: '100%',
                                      width: `${Math.min(100, rule.actual)}%`,
                                      bgcolor: rule.color,
                                      borderRadius: 4,
                                      transition: 'width .4s ease',
                                    }}
                                  />
                                  {rule.guideline > 0 && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: -1,
                                        left: `${rule.guideline}%`,
                                        width: 2,
                                        height: 10,
                                        bgcolor: 'text.disabled',
                                        borderRadius: 1,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            ))}
                            <Typography sx={{ fontSize: '.6rem', opacity: 0.4, textAlign: 'center', mt: 0.5 }}>
                              Guideline: {100 - savingsTarget}% expenses, {savingsTarget}% savings
                            </Typography>
                          </Box>
                        </div>

                      </div>
                    </div>

                  </div>
                </div>
              )}

              {!summaryLoading && summaryError && (
                <div className="text-center mt-2 small text-muted" style={{ opacity: .65 }}>
                  Server summary unavailable; using client calculations.
                </div>
              )}
              <div><hr /></div>
            </div>
          </Paper>
        </div>
      </div>
    </div>
  );
}
